/* Generated from Java with JSweet 2.3.5 - http://www.jsweet.org */
import { Library } from '../../Library';
import { ModelUtils } from '../util/ModelUtils';
import { LoggerCompat } from '../../compat/LoggerCompat';
import { NodeCompat } from '../../compat/NodeCompat';
import { Document } from '../../core/models/Document';
import { NodePath } from '../../core/models/NodePath';
import { OasSchema } from '../../openapi/models/OasSchema';
import { Oas30Schema } from '../../openapi/v3/models/Oas30Schema';
import { AbstractSchemaInhCommand } from './AbstractSchemaInhCommand';
import { MarshallCompat } from '../../compat/MarshallCompat';
import { Node } from '../../core/models/Node';

/**
 * Allows changing the inheritance setting for a schema.  This basically allows changing
 * the usage of "anyOf", "allOf", etc.  The algorithm for this command is as follows:
 * 
 * - When switching from "none" to any inheritance mode, any existing properties of the
 * schema will be wrapped in a new schema and added to the new inheritance mode
 * - When switching from any inheritance mode back to "none", any properties in a wrapped
 * schema will be unwrapped and added as properties
 * - When switching from one inheritance mode to another, the list of schemas will be
 * copied over with it (e.g. when going from "allOf" to "anyOf")
 * 
 * Challenge: what impact will this have on undo/redo?  Changes made to properties after
 * this command is executed may be lost when this command is undone!
 * 
 * @author eric.wittmann@gmail.com
 * @extends AbstractSchemaInhCommand
 * @class
 */
export class ChangeSchemaInheritanceCommand extends AbstractSchemaInhCommand {
    public _schemaPath : NodePath;

    public _newInheritanceType : string;

    public _oldInheritanceType : string;

    public _oldSchemas : Array<any>;

    public constructor(schema? : any, inheritanceType? : any) {
        if(((schema != null && schema instanceof <any>OasSchema) || schema === null) && ((typeof inheritanceType === 'string') || inheritanceType === null)) {
            let __args = arguments;
            super();
            if(this._schemaPath===undefined) this._schemaPath = null;
            if(this._newInheritanceType===undefined) this._newInheritanceType = null;
            if(this._oldInheritanceType===undefined) this._oldInheritanceType = null;
            if(this._oldSchemas===undefined) this._oldSchemas = null;
            if(this._schemaPath===undefined) this._schemaPath = null;
            if(this._newInheritanceType===undefined) this._newInheritanceType = null;
            if(this._oldInheritanceType===undefined) this._oldInheritanceType = null;
            if(this._oldSchemas===undefined) this._oldSchemas = null;
            (() => {
                this._schemaPath = Library.createNodePath(schema);
                this._newInheritanceType = inheritanceType;
            })();
        } else if(schema === undefined && inheritanceType === undefined) {
            let __args = arguments;
            super();
            if(this._schemaPath===undefined) this._schemaPath = null;
            if(this._newInheritanceType===undefined) this._newInheritanceType = null;
            if(this._oldInheritanceType===undefined) this._oldInheritanceType = null;
            if(this._oldSchemas===undefined) this._oldSchemas = null;
            if(this._schemaPath===undefined) this._schemaPath = null;
            if(this._newInheritanceType===undefined) this._newInheritanceType = null;
            if(this._oldInheritanceType===undefined) this._oldInheritanceType = null;
            if(this._oldSchemas===undefined) this._oldSchemas = null;
        } else throw new Error('invalid overload');
    }

    /**
     * @see io.apicurio.datamodels.cmd.ICommand#execute(io.apicurio.datamodels.core.models.Document)
     * @param {Document} document
     */
    public execute(document : Document) {
        LoggerCompat.info("[ChangeSchemaInheritanceCommand] Executing: " + this._newInheritanceType);
        this._oldInheritanceType = null;
        this._oldSchemas = null;
        let schema : OasSchema = <OasSchema>this._schemaPath.resolve(document);
        if(this.isNullOrUndefined(schema)) {
            return;
        }
        this._oldInheritanceType = AbstractSchemaInhCommand.getInheritanceType(schema);
        this.switchInheritance(schema, this._oldInheritanceType, this._newInheritanceType, false);
    }

    /**
     * @see io.apicurio.datamodels.cmd.ICommand#undo(io.apicurio.datamodels.core.models.Document)
     * @param {Document} document
     */
    public undo(document : Document) {
        LoggerCompat.info("[ChangeSchemaInheritanceCommand] Reverting: " + this._oldInheritanceType);
        let schema : OasSchema = <OasSchema>this._schemaPath.resolve(document);
        if(this.isNullOrUndefined(schema)) {
            return;
        }
        this.switchInheritance(schema, this._newInheritanceType, this._oldInheritanceType, true);
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_NONE, this._newInheritanceType) && !/* isEmpty */(this._oldSchemas.length == 0)) {
            this.copySchemaJsTo(this._oldSchemas, schema, this._oldInheritanceType);
        }
    }

    /**
     * Switch the inheritance type.
     * @param {OasSchema} schema
     * @param {string} fromType
     * @param {string} toType
     * @param {boolean} isUndo
     * @private
     */
    /*private*/ switchInheritance(schema : OasSchema, fromType : string, toType : string, isUndo : boolean) {
        if(NodeCompat.equals(fromType, toType)) {
            return;
        }
        let schemas : Array<OasSchema> = <any>([]);
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_ALL_OF, fromType)) {
            schemas = schema.allOf;
            schema.allOf = null;
        }
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_ANY_OF, fromType)) {
            let schema30 : Oas30Schema = <Oas30Schema>schema;
            schemas = schema30.anyOf;
            schema30.anyOf = null;
        }
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_ONE_OF, fromType)) {
            let schema30 : Oas30Schema = <Oas30Schema>schema;
            schemas = schema30.oneOf;
            schema30.oneOf = null;
        }
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_NONE, toType)) {
            if(!isUndo) {
                this._oldSchemas = <any>([]);
            }
            schemas.forEach((s) => {
                if(ModelUtils.isDefined(s.properties)) {
                    this.moveProperties(s, schema);
                } else if(!isUndo) {
                    /* add */(this._oldSchemas.push(Library.writeNode(s))>0);
                }
            });
        }
        if(NodeCompat.equals(AbstractSchemaInhCommand.TYPE_NONE, fromType)) {
            let wrapperSchema : OasSchema = this.createSchema(schema, fromType);
            wrapperSchema.type = "object";
            this.moveProperties(schema, wrapperSchema);
            /* add */(schemas.push(wrapperSchema)>0);
        }
        if(!/* isEmpty */(schemas.length == 0)) {
            this.copySchemasTo(schemas, schema, toType);
        }
    }

    /**
     * Copies the given list of schemas to the appropriate property on the model
     * @param {OasSchema[]} schemas
     * @param {OasSchema} targetSchema
     * @param {string} inheritanceType
     * @private
     */
    /*private*/ copySchemasTo(schemas : Array<OasSchema>, targetSchema : OasSchema, inheritanceType : string) {
        let jsSchemas : Array<any> = <any>([]);
        schemas.forEach(((jsSchemas) => {
            return (schema) => {
                /* add */(jsSchemas.push(Library.writeNode(schema))>0);
            }
        })(jsSchemas));
        this.copySchemaJsTo(jsSchemas, targetSchema, inheritanceType);
    }

    /**
     * Moves properties from one schema to another.
     * @param {OasSchema} from
     * @param {OasSchema} to
     * @private
     */
    /*private*/ moveProperties(from : OasSchema, to : OasSchema) {
        to.properties = from.properties;
        from.properties = null;
        /* values */(obj => Object.keys(obj).map(key => obj[key]))(to.properties).forEach((pschema) => {
            pschema._parent = to;
        });
        to.required = from.required;
        from.required = null;
    }
}
ChangeSchemaInheritanceCommand["__class"] = "io.apicurio.datamodels.cmd.commands.ChangeSchemaInheritanceCommand";
ChangeSchemaInheritanceCommand["__interfaces"] = ["io.apicurio.datamodels.cmd.ICommand"];




